import React, { useCallback } from 'react';

import { AlineTransforms, assetsArrayToImageMap, commentMarkdownToPlainText } from '@aline/common';
import type { DocParts } from '@aline/common';
import type { DocxSerializer } from '@aline/common/serializers/docx/index';
import { useSnackbar } from 'notistack';
import { LoadingSnackbar } from 'src/components';
import { ClientTransforms } from 'src/editor/transforms';
import { useDocumentParts } from 'src/hooks';
import { v4 as uuid4 } from 'uuid';

import { ExportDocumentCommentsMode, filterCommentsForExport } from './filterCommentsForExport';
import type { DocumentPartComment } from './filterCommentsForExport';

export function useDownloadDOCX() {
  const { getDocumentParts } = useDocumentParts();
  const downloadDOCX = useCallback(
    async (
      documentId: UUID,
      options: {
        docParts: DocParts;
        name: string;
        commentsMode?: ExportDocumentCommentsMode;
      }
    ) => {
      const { commentsMode = ExportDocumentCommentsMode.PublicOnly, docParts, name } = options;
      const editor = AlineTransforms.createServerEditorFromJson(docParts, {
        state: {
          editingUserId: null,
          isInSigningCeremony: false,
          isReadOnly: false,
          isSigned: false,
        },
      });
      const { assets, authors: documentAuthors, comments } = await getDocumentParts(documentId);
      const imageMap = await assetsArrayToImageMap(assets);
      const commentsForExport = filterCommentsForExport(comments, commentsMode);
      const dbComments = buildDbCommentsFromGqlComments(commentsForExport);

      await ClientTransforms.exportToDocx(editor, {
        dbComments,
        documentAuthors,
        documentId,
        imageMap,
        name,
      });
    },
    [getDocumentParts]
  );
  return { downloadDOCX };
}

export function useDownloadDOCXWithLoading() {
  const { downloadDOCX } = useDownloadDOCX();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const downloadDOCXWithLoading = useCallback(
    async (...args: Parameters<typeof downloadDOCX>) => {
      const loadSnackKey = uuid4();
      enqueueSnackbar('', {
        content: <LoadingSnackbar id={loadSnackKey} message="Downloading DOCX..." />,
        key: loadSnackKey,
        persist: true,
      });
      const res = await downloadDOCX(...args);
      closeSnackbar(loadSnackKey);
      return res;
    },
    [closeSnackbar, downloadDOCX, enqueueSnackbar]
  );
  return { downloadDOCXWithLoading };
}

function toDbComment(
  comment: DocumentPartComment | DocumentPartComment['replies'][number],
  parentCommentId: string | null
): DocxSerializer.DBComment {
  return {
    authorId: comment.author?.id ?? null,
    createdAt: comment.createdAt,
    id: comment.id,
    importedAuthorName: comment.importedAuthorName,
    nodeRefId: comment.nodeRefId,
    parentCommentId,
    resolved: comment.resolved,
    text: commentMarkdownToPlainText(comment.text),
  };
}

function buildDbCommentsFromGqlComments(
  comments: DocumentPartComment[]
): DocxSerializer.DBComment[] {
  return comments.flatMap(comment => [
    toDbComment(comment, comment.parent?.id ?? null),
    ...comment.replies.map(reply => toDbComment(reply, comment.id)),
  ]);
}
